import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { getMe, getPublicSettings, PaymentAsset, PaymentNetwork, PublicSettings, UserProfile } from '../api/api';

export type OrderStatus = 'invoice_created' | 'paid' | 'processing' | 'completed' | 'failed' | 'expired';

const USER_TOKEN_KEY = 'user_token';

interface AuthState {
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
}

interface AppContextType {
  userAuth: AuthState & { user: UserProfile | null; setUser: (user: UserProfile | null) => void; refreshUser: () => Promise<void> };
  settings: PublicSettings | null;
  refreshSettings: (params?: { asset?: PaymentAsset; network?: PaymentNetwork }) => Promise<void>;
}

const AppContext = createContext<AppContextType | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const [userToken, setUserToken] = useState<string | null>(() => localStorage.getItem(USER_TOKEN_KEY));
  const [user, setUser] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<PublicSettings | null>(null);
  const latestSettingsRequest = useRef(0);

  const fetchSettings = useCallback(async (params?: { asset?: PaymentAsset; network?: PaymentNetwork }) => {
    const requestId = latestSettingsRequest.current + 1;
    latestSettingsRequest.current = requestId;
    try {
      const result = await getPublicSettings(params);
      if (requestId === latestSettingsRequest.current) setSettings(result);
    } catch {
      if (requestId === latestSettingsRequest.current) {
        // Backend unavailable — use sensible defaults
        setSettings({ liquidity: 0, turnover: 0, rate: 90, commissionDefault: 0.09, commissionPromo: 0.07, maintenanceMode: false });
      }
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const refreshUser = useCallback(async () => {
    if (!userToken) { setUser(null); return; }
    try {
      setUser(await getMe(userToken));
    } catch {
      localStorage.removeItem(USER_TOKEN_KEY);
      setUserToken(null);
      setUser(null);
    }
  }, [userToken]);

  useEffect(() => { refreshUser(); }, [refreshUser]);

  const userAuth = {
    token: userToken,
    user,
    setUser,
    refreshUser,
    login: (t: string) => { localStorage.setItem(USER_TOKEN_KEY, t); setUserToken(t); },
    logout: () => { localStorage.removeItem(USER_TOKEN_KEY); setUserToken(null); setUser(null); },
  };

  return (
    <AppContext.Provider value={{ userAuth, settings, refreshSettings: fetchSettings }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
